import React, { useEffect } from "react";
import { Box, Button } from "@mui/material";
import { useModel } from "hooks/useModel";
import styles from "./ChatArea.module.scss";
import { Cookies } from "utils";
import { FIRST_NAME_COOKIE } from "constant";
import ArrowRightAltOutlinedIcon from "@mui/icons-material/ArrowRightAltOutlined";
import { fetchPrompts } from "../../store/PromptActions";
import { useAxios } from "myAxios";
import { getIconComponent } from "../Prompts/PromptsCardList";
import ChatAreaInput from "../ChatInput/ChatAreaInput";
import { createClassicAccountTag, useAppActions } from "components/ai-chat/store/AppStore";
import { DATA_TYPE_OPTIONS } from "components/ai-chat/constants";
import { useRouter } from "next/router";
import { InfoType } from "components/ai-chat/types";

const ChatAreaDefault = () => {
  const { prompts, setPrompts, setPromptsOpen, setFullScreen, selectedKey, currentAccountTags } =
    useModel("aichat");
  const axios = useAxios();
  const { getLoadedMessage, sendMessageFn } = useAppActions(axios);
  const [quickPrompts, setQuickPrompts] = React.useState<any[]>([]);
  const firstName = Cookies.get(FIRST_NAME_COOKIE);
  const router = useRouter();

  useEffect(() => {
    const systemTab = prompts.find((item) => item.type === "system");
    const favoriteTab = prompts.find((item) => item.type === "favorite");
    if (!favoriteTab || !systemTab) return;
    if (favoriteTab.list.length === 0 && systemTab.list.length === 0) {
      Promise.all([
        fetchPrompts(axios, {
          pageNum: 0,
          pageSize: 15,
          favorite: true,
        }),
        fetchPrompts(axios, {
          pageNum: 0,
          pageSize: 15,
          promptType: "system",
        }),
      ]).then(([res1, res2]) => {
        const merged = [...res1.prompts, ...res2.prompts];
        const unique = Array.from(new Map(merged.map((item) => [item.id, item])).values());
        setQuickPrompts(unique);
        setPrompts((prev) =>
          prev.map((item) => {
            if (item.type === "system") {
              return {
                ...item,
                list: Array.from(
                  new Map([...item.list, ...res2.prompts].map((f) => [f.id, f])).values()
                ) as any,
                hasMore: res2.hasMore,
                nextpage: 1,
              };
            }
            if (item.type === "favorite") {
              return {
                ...item,
                list: Array.from(
                  new Map([...item.list, ...res1.prompts].map((f) => [f.id, f])).values()
                ) as any,
                hasMore: res1.hasMore,
                nextpage: 1,
              };
            }
            return item;
          })
        );
      });
    } else {
      const merged = [...favoriteTab.list, ...systemTab.list];
      const unique = Array.from(new Map(merged.map((item) => [item.id, item])).values());
      setQuickPrompts(unique);
    }
  }, [prompts.find((item) => item.type === "favorite")]);

  useEffect(() => {
    if (router.query?.accountId && !selectedKey) {
      axios.get(`/api/dataservice/v1/accounts/${router.query.accountId}`).then((res) => {
        const { accountId, accountName } = res.data;
        const rows: InfoType[] = [
          createClassicAccountTag({
            id: accountId,
            name: accountName,
            selectedList: DATA_TYPE_OPTIONS,
          }),
        ];
        getLoadedMessage(rows, [], selectedKey);
      });
    }
  }, [router.query]);

  return (
    <Box className={styles.default}>
      <Box style={{ height: 164 }}>
        <img
          src="/aichat/chat.svg"
          alt=""
        />
        <Box className={styles.defaultTitle}>
          Hi, {firstName}
          <br />
          How can I assist you today?
        </Box>
        <Box className={styles.description}>
          Send a message or use one of the quick prompts below
        </Box>
      </Box>
      <Box className={styles.textarea}>
        <ChatAreaInput />
      </Box>
      <Box className={styles.quickPrompts}>
        <Box className={styles.promptsButtonGroup}>
          {quickPrompts.slice(0, 3).map((item) => (
            <Button
              variant="outlined"
              key={item.promptTitle}
              onClick={() => {
                const prompt = {
                  description: item.promptDescription,
                  title: item.promptTitle,
                  prompt: item.promptContent,
                };
                sendMessageFn(item.promptContent, undefined, currentAccountTags, prompt);
              }}
            >
              <div
                style={{
                  marginRight: 2,
                  fontSize: 16,
                  paddingTop: 2,
                }}
              >
                {getIconComponent(item?.promptIcon)}
              </div>{" "}
              {item.promptTitle}
            </Button>
          ))}
        </Box>
        <Box className={styles.viewMore}>
          <Button
            variant="text"
            onClick={() => {
              setFullScreen(true);
              setPromptsOpen(true);
            }}
          >
            View more prompts
            <ArrowRightAltOutlinedIcon fontSize="small" />
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ChatAreaDefault;
